import { HealthResponseSchema, type HealthResponse } from '@tcg/protocol';

/** Ruta del health check en el servidor. */
export const HEALTH_PATH = '/health';

/**
 * Presupuesto total de la sonda.
 *
 * Render Free apaga el servicio tras 15 minutos sin trafico y tarda **cerca de
 * un minuto** en volver a levantarlo. 120 s dejan margen de sobra para ese
 * arranque en frio sin dejar la pantalla colgada indefinidamente.
 */
export const TOTAL_BUDGET_MS = 120_000;

/**
 * A partir de aqui se asume arranque en frio y la interfaz lo dice.
 *
 * Un servidor ya despierto responde en decimas de segundo; si a los 8 s aun no
 * ha contestado, casi con seguridad se esta levantando.
 */
export const COLD_START_HINT_MS = 8_000;

/** Timeout de cada intento individual. */
export const REQUEST_TIMEOUT_MS = 12_000;

const MAX_BACKOFF_MS = 4_000;

export type ProbePhase = 'idle' | 'probing' | 'waking' | 'healthy' | 'unreachable';

export interface ProbeState {
  readonly phase: ProbePhase;
  /** Numero de intentos realizados. */
  readonly attempt: number;
  /** Milisegundos transcurridos desde el primer intento. */
  readonly elapsedMs: number;
  /** Payload validado, solo cuando `phase === 'healthy'`. */
  readonly payload: HealthResponse | null;
  /** Ultimo error observado, en texto legible. */
  readonly error: string | null;
}

export const IDLE_STATE: ProbeState = {
  phase: 'idle',
  attempt: 0,
  elapsedMs: 0,
  payload: null,
  error: null,
};

/**
 * Convierte la URL del servidor de juego en su base HTTP.
 *
 * `VITE_SERVER_URL` apunta al WebSocket (`ws://`/`wss://`), pero el health
 * check es HTTP. Los esquemas se corresponden uno a uno, asi que basta con
 * traducirlos en lugar de exigir una segunda variable.
 */
export function toHttpBaseUrl(serverUrl: string): string {
  const url = new URL(serverUrl);

  if (url.protocol === 'ws:') {
    url.protocol = 'http:';
  } else if (url.protocol === 'wss:') {
    url.protocol = 'https:';
  }

  return url.origin;
}

/** Construye la URL absoluta del health check. */
export function healthEndpoint(httpBaseUrl: string): string {
  return new URL(HEALTH_PATH, httpBaseUrl).toString();
}

/** Espera entre intentos: creciente y con tope, para no martillear el servidor. */
export function backoffDelayMs(attempt: number): number {
  return Math.min(attempt * 1_000, MAX_BACKOFF_MS);
}

/** Decide la fase mientras la sonda sigue en curso. */
export function phaseWhilePending(elapsedMs: number, coldStartHintMs: number): ProbePhase {
  return elapsedMs >= coldStartHintMs ? 'waking' : 'probing';
}

function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ProbeDeps {
  readonly fetchImpl: FetchLike;
  readonly now: () => number;
  readonly sleep: (ms: number) => Promise<void>;
}

export interface ProbeOptions {
  readonly httpBaseUrl: string;
  readonly signal?: AbortSignal;
  readonly totalBudgetMs?: number;
  readonly coldStartHintMs?: number;
  readonly requestTimeoutMs?: number;
  readonly onState?: (state: ProbeState) => void;
}

/**
 * Sondea `GET /health` hasta que responda correctamente o se agote el
 * presupuesto.
 *
 * Todas las dependencias temporales y de red se inyectan, de modo que la
 * funcion es determinista y se puede probar sin red, sin DOM y sin timers
 * reales. Nunca lanza por un fallo de red: devuelve `unreachable`.
 */
export async function probeUntilHealthy(
  options: ProbeOptions,
  deps: ProbeDeps,
): Promise<ProbeState> {
  const {
    httpBaseUrl,
    signal,
    totalBudgetMs = TOTAL_BUDGET_MS,
    coldStartHintMs = COLD_START_HINT_MS,
    requestTimeoutMs = REQUEST_TIMEOUT_MS,
    onState,
  } = options;

  const endpoint = healthEndpoint(httpBaseUrl);
  const startedAt = deps.now();
  let attempt = 0;
  let lastError = 'Sin respuesta del servidor.';

  const elapsed = (): number => deps.now() - startedAt;

  const emit = (state: ProbeState): ProbeState => {
    onState?.(state);
    return state;
  };

  while (!signal?.aborted) {
    attempt += 1;
    emit({
      phase: phaseWhilePending(elapsed(), coldStartHintMs),
      attempt,
      elapsedMs: elapsed(),
      payload: null,
      error: attempt > 1 ? lastError : null,
    });

    // Timeout por intento: sin el, una conexion colgada consumiria el
    // presupuesto completo en un unico intento y nunca reintentaria.
    const timeoutSignal = AbortSignal.timeout(requestTimeoutMs);
    const requestSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

    try {
      const response = await deps.fetchImpl(endpoint, {
        method: 'GET',
        headers: { accept: 'application/json' },
        cache: 'no-store',
        signal: requestSignal,
      });

      if (!response.ok) {
        lastError = `El servidor respondio HTTP ${String(response.status)}.`;
      } else {
        const parsed = HealthResponseSchema.safeParse(await response.json());
        if (parsed.success) {
          return emit({
            phase: 'healthy',
            attempt,
            elapsedMs: elapsed(),
            payload: parsed.data,
            error: null,
          });
        }
        lastError = 'El payload de /health no cumple el contrato de @tcg/protocol.';
      }
    } catch (error) {
      lastError = timeoutSignal.aborted
        ? `El intento supero ${String(requestTimeoutMs)} ms sin respuesta.`
        : describeError(error);
    }

    const delay = backoffDelayMs(attempt);
    if (signal?.aborted || elapsed() + delay >= totalBudgetMs) {
      break;
    }

    await deps.sleep(delay);
  }

  return emit({
    phase: 'unreachable',
    attempt,
    elapsedMs: elapsed(),
    payload: null,
    error: lastError,
  });
}

/** Dependencias reales: `fetch`, el reloj del navegador y `setTimeout`. */
export function createBrowserProbeDeps(): ProbeDeps {
  return {
    fetchImpl: (input, init) => fetch(input, init),
    now: () => Date.now(),
    sleep: (ms) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      }),
  };
}
